import React from 'react';
import { View, Text, Pressable, StyleSheet } from 'react-native';
import { useTranslation } from 'react-i18next';
import Feather from '@expo/vector-icons/Feather';
import { Paprika, PaprikaShadows } from '../../styles/theme';
import { ImportBatch } from './importBatch';

interface Props {
  batch: ImportBatch;
  onPress: () => void;
  onDismiss: () => void;
}

/**
 * "Panier Lufa du 24 août — 21 aliments ajoutés".
 *
 * The only trace an email import leaves in the app. It is deliberately
 * dismissible and deliberately short-lived (see IMPORT_BANNER_DAYS): the items
 * are already in the pantry, so this is an offer to look, never a task.
 */
export function PantryImportBanner({ batch, onPress, onDismiss }: Props) {
  const { t, i18n } = useTranslation();

  const date = batch.deliveryDate ? formatDeliveryDate(batch.deliveryDate, i18n.language) : '';
  const title = date ? t('pantry.import.title', { date }) : t('pantry.import.titleNoDate');

  return (
    <Pressable onPress={onPress} style={styles.card} accessibilityRole="button">
      <View style={styles.dot} />
      <View style={styles.body}>
        <Text style={styles.title} numberOfLines={1}>
          {title}
        </Text>
        <Text style={styles.subtitle} numberOfLines={1}>
          {t('pantry.import.subtitle', { count: batch.count })}
        </Text>
      </View>
      <Text style={styles.cta}>{t('pantry.import.cta')}</Text>
      <Pressable
        onPress={onDismiss}
        hitSlop={10}
        style={styles.dismiss}
        accessibilityLabel={t('pantry.import.dismiss')}
      >
        <Feather name="x" size={16} color={Paprika.textMuted} />
      </Pressable>
    </Pressable>
  );
}

/** "5 octobre" — a delivery date as the banner and the basket review print it. */
export function formatDeliveryDate(isoDate: string, locale: string): string {
  const [year, month, day] = isoDate.split('-').map(Number);
  if (!year || !month || !day) return '';
  return new Date(year, month - 1, day).toLocaleDateString(locale, {
    month: 'long',
    day: 'numeric',
  });
}

const styles = StyleSheet.create({
  card: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 10,
    marginHorizontal: 16,
    marginBottom: 8,
    paddingVertical: 11,
    paddingHorizontal: 12,
    borderRadius: 14,
    backgroundColor: Paprika.surfaceLowest,
    ...PaprikaShadows.card,
  },
  dot: {
    width: 8,
    height: 8,
    borderRadius: 999,
    backgroundColor: Paprika.accent,
  },
  body: {
    flex: 1,
    minWidth: 0,
  },
  title: {
    fontFamily: 'Inter-SemiBold',
    fontSize: 13,
    color: Paprika.textPrimary,
    lineHeight: 18,
  },
  subtitle: {
    fontFamily: 'Inter-Regular',
    fontSize: 11.5,
    color: Paprika.textMuted,
    lineHeight: 16,
  },
  cta: {
    fontFamily: 'Inter-SemiBold',
    fontSize: 12,
    color: Paprika.accent,
  },
  dismiss: {
    padding: 2,
  },
});
